import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

interface ActivityData {
  date: string;
  activity: string;
  type: 'compliance' | 'audit' | 'review' | 'submission';
  status: 'completed' | 'pending' | 'overdue';
}

interface ActivityTimelineChartProps {
  data: ActivityData[];
  title?: string;
  height?: number;
}

const ActivityTimelineChart: React.FC<ActivityTimelineChartProps> = ({
  data,
  title = 'Activity Timeline',
  height = 300
}) => {
  const getActivityColor = (type: string) => {
    const baseColors = {
      compliance: '#2563eb',
      audit: '#dc2626',
      review: '#16a34a',
      submission: '#ca8a04'
    };

    return baseColors[type as keyof typeof baseColors] || '#6b7280';
  };

  const getActivitySize = (status: string) => {
    return status === 'completed' ? 8 : status === 'pending' ? 6 : 4;
  };

  const chartData = {
    labels: data.map(activity => new Date(activity.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: 'Activities',
        data: data.map((activity, index) => ({
          x: new Date(activity.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          y: index % 2 === 0 ? 1 : 2, // Alternate between two lines for visual separation
          activity: activity.activity,
          type: activity.type,
          status: activity.status
        })),
        backgroundColor: data.map(activity => getActivityColor(activity.type)),
        borderColor: data.map(activity => getActivityColor(activity.type)),
        borderWidth: 2,
        pointRadius: data.map(activity => getActivitySize(activity.status)),
        pointHoverRadius: data.map(activity => getActivitySize(activity.status) + 2),
        showLine: false,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 16,
          weight: 'bold' as const
        },
        padding: {
          top: 10,
          bottom: 30
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: function(context: any) {
            return context[0].label || 'Activity';
          },
          label: function(context: any) {
            const data = context.raw;
            return [
              `Activity: ${data.activity}`,
              `Type: ${data.type.charAt(0).toUpperCase() + data.type.slice(1)}`,
              `Status: ${data.status.charAt(0).toUpperCase() + data.status.slice(1)}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        type: 'category' as const,
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 14,
            weight: 500
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      y: {
        display: false,
        min: 0,
        max: 3
      }
    },
    elements: {
      point: {
        hoverRadius: 8
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div style={{ height: `${height}px` }}>
        <Scatter data={chartData} options={options} />
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-4 mt-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-600"></div>
          <span>Compliance</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-600"></div>
          <span>Audit</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-600"></div>
          <span>Review</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-600"></div>
          <span>Submission</span>
        </div>
      </div>
    </div>
  );
};

export default ActivityTimelineChart;